import type {
  VisualPayload,
  SeasonOverview,
  CupSlot,
} from "../../types";
import CardShell from "./_CardShell";
import {
  SeasonHeaderStrip,
  SeasonSourceLink,
  CupWeekBlock,
  groupCupsByWeek,
} from "./_seasonShared";

interface Props { payload: VisualPayload }

/**
 * Dedicated cup-rotation render.  Always shows the full schedule (no
 * collapse — the rotation IS the content).  Groups concurrent cups by
 * week (multi-cup weeks render under a single date header) and lifts
 * the `now` / `next` highlight badge to the week level based on
 * today's date.
 */
export default function CupScheduleCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const overview = (sections.find((s) => s.section === "season_overview") ?? {}) as SeasonOverview;
  const cupScheduleSec = sections.find((s) => s.section === "cup_schedule");
  const cups = (cupScheduleSec?.items as CupSlot[] | undefined) ?? [];
  const cupWeeks = groupCupsByWeek(cups);

  // Find the active and next weeks against today.  Lifted to week-level
  // (not individual cup-level) because concurrent cups in the same week
  // share the same activity status.
  const nowMs = new Date().getTime();
  let activeIdx = -1;
  let nextIdx = -1;
  cupWeeks.forEach((w, i) => {
    if (!w.week_start) return;
    const start = new Date(w.week_start.length === 10 ? `${w.week_start}T00:00:00Z` : w.week_start).getTime();
    const end = w.week_end
      ? new Date(w.week_end.length === 10 ? `${w.week_end}T23:59:59Z` : w.week_end).getTime()
      : start + 7 * 24 * 60 * 60 * 1000;
    if (start <= nowMs && nowMs <= end) activeIdx = i;
    if (start > nowMs && nextIdx === -1) nextIdx = i;
  });

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-indigo-500/20"
    >
      <SeasonHeaderStrip overview={overview} />

      {cupWeeks.length === 0 ? (
        <p className="text-sm text-white/40 py-2">No cup schedule available.</p>
      ) : (
        <ul className="space-y-2">
          {cupWeeks.map((w, i) => {
            const badge =
              i === activeIdx
                ? { label: "now", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" }
                : i === nextIdx
                ? { label: "next", cls: "bg-blue-500/15 text-blue-300 border-blue-500/25" }
                : null;
            return <CupWeekBlock key={i} week={w} badge={badge} />;
          })}
        </ul>
      )}

      <SeasonSourceLink sourceUrl={payload.source_url} />
    </CardShell>
  );
}
